/**
 * Qué se puede decir de las formas de pago de una tienda, y qué no.
 *
 * Son **tres** estados, no dos, y confundir los dos primeros es lo que
 * convierte una mejora en una caja que no abre:
 *
 * | Estado | Qué significa | Qué se hace |
 * |---|---|---|
 * | `SIN_DATO` | Nunca se pudo leer `FormasPagoTienda` | **Nada.** Se ofrece el catálogo entero, como siempre |
 * | `SIN_CONFIGURAR` | Sí se leyó, y esta tienda no tiene ninguna | Se avisa y no se abre la caja |
 * | `CONFIGURADAS` | Sí se leyó, y tiene éstas | Se ofrecen sólo ésas |
 *
 * `SIN_DATO` es el que permite que el terminal se despliegue antes que el
 * servicio web, o en un ambiente donde nadie lo haya registrado todavía, sin
 * dejar a ninguna tienda sin cobrar. Un terminal que no sabe no manda.
 */

export const SIN_DATO = "sin_dato";
export const SIN_CONFIGURAR = "sin_configurar";
export const CONFIGURADAS = "configuradas";

/**
 * @param {{seLeyo: boolean, codigos: string[]}} datos
 *   `seLeyo` es si la entidad llegó a leerse alguna vez con éxito; `codigos`,
 *   los de esa tienda.
 */
export const estadoDeLasFormasDePago = ({ seLeyo, codigos } = {}) => {
  if (!seLeyo) return SIN_DATO;

  return (codigos ?? []).length ? CONFIGURADAS : SIN_CONFIGURAR;
};

/**
 * Las formas de pago que se le ofrecen al cajero.
 *
 * Con `SIN_DATO` devuelve el catálogo tal cual: no se recorta lo que no se sabe.
 *
 * @param {object[]} catalogo Las formas de pago de `db.formasPago`.
 * @param {{seLeyo: boolean, codigos: string[]}} datos
 */
export const formasDePagoQueSeOfrecen = (catalogo, datos) => {
  const todas = catalogo ?? [];

  // Sólo el desconocimiento devuelve el catálogo entero. Una tienda **leída y
  // sin ninguna** ofrece cero, aunque incomode: ofrecerle al cajero una forma
  // de pago que Business Central va a rechazar al registrar cambia un problema
  // visible al instante por una venta que revienta después y deja el borrador
  // varado. Es el mismo criterio que la apertura.
  if (estadoDeLasFormasDePago(datos) === SIN_DATO) return todas;

  const permitidas = new Set((datos?.codigos ?? []).map((c) => String(c).trim().toUpperCase()));

  return todas.filter((forma) =>
    permitidas.has(
      String(forma?.Code ?? forma?.No ?? "")
        .trim()
        .toUpperCase()
    )
  );
};

/**
 * El aviso de que no se puede abrir la caja, según por qué.
 *
 * Son dos mensajes y no uno porque **los arreglan personas distintas**: uno lo
 * resuelve quien configura Business Central, el otro se resuelve con red. Un
 * único texto genérico manda al cajero a buscar a la persona equivocada.
 *
 * @param {string} tienda El código de tienda de la caja.
 * @param {string} estado Lo que devolvió `estadoDeLasFormasDePago`.
 */
export const avisoDeAperturaSinFormasDePago = (tienda, estado) => {
  if (estado !== SIN_CONFIGURAR) return "";

  const cual = String(tienda ?? "").trim();

  return (
    `No se puede abrir la caja: la tienda ${cual || "de esta caja"} no tiene formas de pago ` +
    "configuradas en Business Central, así que no se podría cobrar. " +
    "Avisa a quien configura Business Central."
  );
};
